/**
 * Video Studio documents (`*.video.json`, format `prism.video/1`).
 *
 * A video is a storyboard: a story bible, characters (look + voice), locations,
 * and acts -> scenes -> shots. Each shot is made by local AI video models
 * (type "ai") or recorded from the engine with a sequence (type "engine").
 * Every piece is a generation request that goes through the normal approval,
 * queue and review: the doc only remembers which request made which file
 * (`StageRecord`), and the status is read from the request.
 *
 * Pure: no IO. Shared by the worker (planning, tools), the server and the editor.
 */

import type { GenRequest, GenStatus } from './generation.js';

export const VIDEO_FORMAT = 'prism.video/1';
export const VIDEO_EXT = '.video.json';

export type VideoMode = 'ai' | 'engine' | 'mixed';
export type ShotType = 'ai' | 'engine';
/** Where a shot's first frame comes from. */
export type Continuity = 'previous-last-frame' | 'keyframe' | 'first-last';
export type TransitionType = 'cut' | 'crossfade' | 'fade-black';
export type VoiceEngine = 'kokoro' | 'chatterbox';
export type RenderQuality = 'draft' | 'standard' | 'high';
export type KeyframeStyle = 'photo' | 'photo-fast' | 'illustration';

/** Status of one piece (derived from its generation request). */
export type StageState = 'none' | 'awaiting-approval' | 'queued' | 'running' | 'review' | 'done' | 'denied' | 'failed';

/** Which request made a piece, and the cached status (the request file is the truth). */
export interface StageRecord {
  readonly request?: string;
  readonly file?: string;
  readonly status?: StageState;
  readonly by?: 'human' | 'assistant' | 'auto';
  readonly updatedAt?: string;
  readonly note?: string;
}

export interface VoiceSpec {
  readonly engine: VoiceEngine;
  /** Kokoro voice id (am_michael, bf_emma...) or Chatterbox reference voice name. */
  readonly voice?: string;
  /** Project .wav to clone (Chatterbox). */
  readonly reference?: string;
  /** Free-form delivery notes ("gravelly, slow"). */
  readonly style?: string;
  readonly speed?: number;
  readonly exaggeration?: number;
  readonly cfg?: number;
  readonly lang?: string;
}

export interface Character {
  readonly id: string;
  readonly name: string;
  /** Visual description used in every prompt the character appears in (face, hair, build, clothes). */
  readonly description: string;
  /** Reference images (project paths); the first is the identity reference (character sheet). */
  readonly refImages?: readonly string[];
  readonly voice?: VoiceSpec;
  /** Fixed seed for the character's stills. */
  readonly seed?: number;
  readonly stages?: { readonly sheet?: StageRecord; readonly voiceTest?: StageRecord };
}

export interface Location {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly refImage?: string;
}

export interface DialogueLine {
  readonly id: string;
  readonly character: string;
  readonly text: string;
  readonly emotion?: string;
  /** Seconds from the shot start (default: after the previous line). */
  readonly offset?: number;
  /** The speaker's face is on camera (lip sync). Default: true when the speaker is in the shot. */
  readonly onCamera?: boolean;
  readonly stages?: { readonly voice?: StageRecord; readonly lipsync?: StageRecord };
}

export interface SfxCue {
  readonly id: string;
  readonly prompt: string;
  readonly preset?: string;
  /** Seconds from the shot start. */
  readonly offset?: number;
  readonly duration?: number;
  readonly volume?: number;
  readonly stages?: { readonly sfx?: StageRecord };
}

/** A key of an engine curve: shot-local time, value, easing (see engine/docs/systems/sequences.md). */
export interface SeqKey {
  readonly t: number;
  readonly v: number | readonly number[];
  readonly ease?: string;
}

export interface CameraSpec {
  /** Shot size ("wide", "medium close-up"). */
  readonly framing?: string;
  /** Movement ("slow dolly in", "static", "handheld"). */
  readonly movement?: string;
  readonly lens?: string;
  /* Engine shots */
  /** Camera entity id in the scene. */
  readonly entity?: string;
  readonly lookAt?: string;
  readonly lookAtOffset?: readonly number[];
  readonly position?: readonly SeqKey[];
  readonly rotation?: readonly SeqKey[];
  readonly fov?: readonly SeqKey[];
  readonly shake?: Record<string, unknown>;
}

export interface EngineAnimation {
  readonly target: string;
  readonly clip: string;
  /** Shot-local seconds. */
  readonly start?: number;
  readonly end?: number;
  readonly clipIn?: number;
  readonly speed?: number;
  readonly loop?: boolean;
  readonly blendIn?: number;
  readonly blendOut?: number;
  readonly weight?: number;
}

export interface EngineShot {
  /** Scene to record (default: the video's `engine.scene`). */
  readonly scene?: string;
  readonly animations?: readonly EngineAnimation[];
  /** Keyed transforms: `{ target, position?, rotation?, scale?, relative? }` with shot-local key times. */
  readonly transforms?: readonly Record<string, unknown>[];
  /** Extra raw prism.sequence/1 tracks with shot-local times (`t`, `start`, `end`, `time` are shifted). */
  readonly tracks?: readonly Record<string, unknown>[];
}

export interface Transition {
  readonly type: TransitionType;
  readonly duration?: number;
}

/** Generator choice per stage (registry ids). */
export interface ShotGenerators {
  readonly animatic?: string;
  readonly final?: string;
  readonly post?: readonly string[];
  readonly keyframe?: readonly string[];
}

export interface Shot {
  readonly id: string;
  readonly title?: string;
  /** Seconds on the timeline. */
  readonly duration: number;
  readonly type: ShotType;
  /** What happens (story beat), in plain words. */
  readonly description: string;
  /** Visual prompt for the video model (built from the description when empty). */
  readonly prompt?: string;
  readonly negative?: string;
  readonly camera?: CameraSpec;
  readonly characters?: readonly string[];
  readonly location?: string;
  readonly dialogue?: readonly DialogueLine[];
  readonly sfx?: readonly SfxCue[];
  /** Music track id playing under this shot (informational; tracks have their own ranges). */
  readonly music?: string;
  readonly continuity?: Continuity;
  /** How this shot joins the previous one. */
  readonly transition?: Transition;
  /** Use this project image as the keyframe instead of generating one. */
  readonly keyframe?: string;
  /** End keyframe image (first-last bridge). */
  readonly endKeyframe?: string;
  /** Short summary of how the shot ends (carried into the next shot's prompt for continuity). */
  readonly summary?: string;
  readonly generators?: ShotGenerators;
  readonly seed?: number;
  readonly engine?: EngineShot;
  readonly stages?: {
    readonly keyframe?: StageRecord;
    readonly endKeyframe?: StageRecord;
    readonly animatic?: StageRecord;
    readonly final?: StageRecord;
    readonly post?: StageRecord;
    readonly engine?: StageRecord;
  };
}

export interface VideoScene {
  readonly id: string;
  readonly title: string;
  readonly synopsis?: string;
  readonly location?: string;
  readonly shots: readonly Shot[];
}

export interface Act {
  readonly id: string;
  readonly title: string;
  readonly summary?: string;
  readonly scenes: readonly VideoScene[];
}

export interface MusicTrack {
  readonly id: string;
  /** Style tags for ACE-Step ("epic orchestral, choir, 90 bpm"). */
  readonly prompt: string;
  readonly style?: string;
  /** Lyrics with [verse]/[chorus] tags, or [instrumental]. */
  readonly lyrics?: string;
  /** Timeline seconds. */
  readonly start: number;
  readonly end?: number;
  readonly volume?: number;
  readonly fadeIn?: number;
  readonly fadeOut?: number;
  /** Lowered under dialogue (default true). */
  readonly duck?: boolean;
  readonly stages?: { readonly music?: StageRecord };
}

export interface AmbienceTrack {
  readonly id: string;
  readonly prompt: string;
  readonly preset?: string;
  readonly start: number;
  readonly end?: number;
  readonly volume?: number;
  readonly loop?: boolean;
  readonly stages?: { readonly sfx?: StageRecord };
}

export interface StoryBible {
  readonly logline: string;
  readonly synopsis: string;
  readonly tone: string;
  readonly visualStyle: string;
  readonly worldRules: readonly string[];
  /** Added to every video negative prompt. */
  readonly negative?: string;
}

export interface RenderSettings {
  readonly quality: RenderQuality;
  /** Re-anchor a chain of shots to a fresh keyframe every N shots (3-4 keeps faces from drifting). */
  readonly reanchorEvery: number;
  readonly animatic: string;
  readonly final: string;
  /** Interpolate low-frame-rate clips (RIFE). */
  readonly interpolate: boolean;
  readonly upscale: 'none' | 'fast' | 'seedvr2';
  readonly subtitles: boolean;
  readonly keyframeStyle: KeyframeStyle;
  /** Use animatic clips in the cut where finals are missing (preview edits). */
  readonly allowDraftClips?: boolean;
}

export interface VideoAudio {
  readonly music: readonly MusicTrack[];
  readonly ambience: readonly AmbienceTrack[];
  readonly dialogueVolume: number;
  readonly musicVolume: number;
  readonly sfxVolume: number;
  /** Integrated loudness target (LUFS). */
  readonly loudness: number;
}

export interface VideoEngineSettings {
  /** Scene the engine shots are recorded in. */
  readonly scene?: string;
  /** Sequence written from the storyboard (default sequences/<name>.sequence.json). */
  readonly sequence?: string;
}

export interface VideoDoc {
  readonly format: typeof VIDEO_FORMAT;
  readonly name: string;
  readonly mode: VideoMode;
  /** The prompt the video was planned from. */
  readonly prompt?: string;
  /** Target length in seconds. */
  readonly targetLength: number;
  readonly width: number;
  readonly height: number;
  readonly fps: number;
  readonly bible: StoryBible;
  readonly characters: readonly Character[];
  readonly locations: readonly Location[];
  readonly acts: readonly Act[];
  readonly audio: VideoAudio;
  readonly render: RenderSettings;
  readonly engine?: VideoEngineSettings;
  /** Folder for every generated file (default videos/<name>). */
  readonly outputDir?: string;
  readonly output?: { readonly file?: string; readonly srt?: string; readonly stages?: { readonly assemble?: StageRecord } };
  /** Free-form editor data. */
  readonly editor?: Record<string, unknown>;
}

/* ------------------------------------------------------------------ */
/* Defaults                                                            */

export const DEFAULT_RENDER: RenderSettings = {
  quality: 'standard',
  reanchorEvery: 3,
  animatic: 'animatic',
  final: 'shot',
  interpolate: true,
  upscale: 'none',
  subtitles: true,
  keyframeStyle: 'photo-fast',
};

export const DEFAULT_AUDIO: VideoAudio = { music: [], ambience: [], dialogueVolume: 1, musicVolume: 0.55, sfxVolume: 0.8, loudness: -16 };

export const RESOLUTIONS: readonly { label: string; width: number; height: number }[] = [
  { label: '480p (832x480)', width: 832, height: 480 },
  { label: '720p (1280x720)', width: 1280, height: 720 },
  { label: '1080p (1920x1080)', width: 1920, height: 1080 },
  { label: '1440p (2560x1440)', width: 2560, height: 1440 },
  { label: '4K (3840x2160)', width: 3840, height: 2160 },
];

/** Keyframe image stages per style (pixel / hi-res registry ids). */
export const KEYFRAME_STAGES: Readonly<Record<KeyframeStyle, readonly string[]>> = {
  'photo-fast': ['photo-realvis-fast'],
  photo: ['photo-realvis'],
  illustration: ['image-sdxl'],
};

/** A stable slug for ids and folder names. */
export function videoSlug(text: string, fallback = 'video'): string {
  const s = text
    .toLowerCase()
    .replace(/\.video\.json$/i, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return s || fallback;
}

/** `videos/intro.video.json` -> `intro`. */
export function videoDocSlug(path: string): string {
  const base = path.replace(/\\/g, '/').split('/').pop() ?? path;
  return videoSlug(base);
}

export function newVideoDoc(name: string, opts: Partial<Pick<VideoDoc, 'mode' | 'targetLength' | 'width' | 'height' | 'fps' | 'prompt'>> & { style?: string } = {}): VideoDoc {
  return {
    format: VIDEO_FORMAT,
    name,
    mode: opts.mode ?? 'ai',
    ...(opts.prompt ? { prompt: opts.prompt } : {}),
    targetLength: opts.targetLength ?? 60,
    width: opts.width ?? 1280,
    height: opts.height ?? 720,
    fps: opts.fps ?? 24,
    bible: { logline: '', synopsis: '', tone: '', visualStyle: opts.style ?? '', worldRules: [] },
    characters: [],
    locations: [],
    acts: [],
    audio: DEFAULT_AUDIO,
    render: DEFAULT_RENDER,
  };
}

/* ------------------------------------------------------------------ */
/* Normalising and validation                                          */

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const s = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d);
const n = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);

/** Fills defaults so code can rely on every collection being present (keeps unknown keys). */
export function normalizeVideoDoc(raw: unknown): VideoDoc {
  const r = obj(raw);
  const bible = obj(r.bible);
  const audio = obj(r.audio);
  const render = obj(r.render);
  const mode = (['ai', 'engine', 'mixed'] as const).includes(r.mode as VideoMode) ? (r.mode as VideoMode) : 'ai';
  return {
    ...(r as object),
    format: VIDEO_FORMAT,
    name: s(r.name, 'Untitled video'),
    mode,
    targetLength: n(r.targetLength, 60),
    width: n(r.width, 1280),
    height: n(r.height, 720),
    fps: n(r.fps, 24),
    bible: {
      ...bible,
      logline: s(bible.logline),
      synopsis: s(bible.synopsis),
      tone: s(bible.tone),
      visualStyle: s(bible.visualStyle),
      worldRules: arr(bible.worldRules).filter((x): x is string => typeof x === 'string'),
    },
    characters: arr(r.characters).map((c) => ({ ...obj(c), id: s(obj(c).id), name: s(obj(c).name), description: s(obj(c).description) })) as Character[],
    locations: arr(r.locations).map((c) => ({ ...obj(c), id: s(obj(c).id), name: s(obj(c).name), description: s(obj(c).description) })) as Location[],
    acts: arr(r.acts).map((a) => ({
      ...obj(a),
      id: s(obj(a).id),
      title: s(obj(a).title),
      scenes: arr(obj(a).scenes).map((sc) => ({
        ...obj(sc),
        id: s(obj(sc).id),
        title: s(obj(sc).title),
        shots: arr(obj(sc).shots).map((sh) => {
          const o = obj(sh);
          return {
            ...o,
            id: s(o.id),
            duration: n(o.duration, 5),
            type: o.type === 'engine' ? 'engine' : 'ai',
            description: s(o.description),
            dialogue: arr(o.dialogue).map((l, i) => ({ ...obj(l), id: s(obj(l).id, `l${i + 1}`), character: s(obj(l).character), text: s(obj(l).text) })),
            sfx: arr(o.sfx).map((c, i) => ({ ...obj(c), id: s(obj(c).id, `fx${i + 1}`), prompt: s(obj(c).prompt) })),
          } as Shot;
        }),
      })) as VideoScene[],
    })) as Act[],
    audio: {
      ...DEFAULT_AUDIO,
      ...audio,
      music: arr(audio.music).map((m, i) => ({ ...obj(m), id: s(obj(m).id, `music${i + 1}`), prompt: s(obj(m).prompt), start: n(obj(m).start, 0) })) as MusicTrack[],
      ambience: arr(audio.ambience).map((m, i) => ({ ...obj(m), id: s(obj(m).id, `amb${i + 1}`), prompt: s(obj(m).prompt), start: n(obj(m).start, 0) })) as AmbienceTrack[],
    } as VideoAudio,
    render: { ...DEFAULT_RENDER, ...render } as RenderSettings,
  } as VideoDoc;
}

const ID = /^[A-Za-z0-9_-]{1,48}$/;

/**
 * Every problem of a video document (empty = valid): format, mode, sizes, unique ids, references to
 * characters / locations / music, engine shots in an AI-only video, durations and dialogue timing.
 */
export function validateVideoDoc(raw: unknown): string[] {
  const errors: string[] = [];
  const r = obj(raw);
  if (r.format !== VIDEO_FORMAT) errors.push(`format must be "${VIDEO_FORMAT}".`);
  if (!['ai', 'engine', 'mixed'].includes(String(r.mode))) errors.push('mode must be "ai", "engine" or "mixed".');
  const doc = normalizeVideoDoc(raw);
  if (doc.targetLength <= 0 || doc.targetLength > 4 * 3600) errors.push('targetLength must be 1 s to 4 h.');
  if (doc.width < 64 || doc.height < 64 || doc.width > 7680 || doc.height > 4320 || doc.width % 2 || doc.height % 2)
    errors.push('width and height must be even numbers from 64 to 7680 x 4320.');
  if (doc.fps < 8 || doc.fps > 120) errors.push('fps must be 8-120.');
  const ids = new Set<string>();
  const unique = (kind: string, id: string) => {
    if (!ID.test(id)) errors.push(`${kind} id "${id}" must be 1-48 letters, digits, _ or -.`);
    else if (ids.has(`${kind}:${id}`)) errors.push(`Duplicate ${kind} id "${id}".`);
    ids.add(`${kind}:${id}`);
  };
  const chars = new Set(doc.characters.map((c) => c.id));
  const locs = new Set(doc.locations.map((l) => l.id));
  const music = new Set(doc.audio.music.map((m) => m.id));
  for (const c of doc.characters) {
    unique('character', c.id);
    if (c.voice && !['kokoro', 'chatterbox'].includes(c.voice.engine)) errors.push(`Character ${c.id}: voice engine must be kokoro or chatterbox.`);
  }
  for (const l of doc.locations) unique('location', l.id);
  for (const a of doc.acts) {
    unique('act', a.id);
    for (const sc of a.scenes) {
      unique('scene', sc.id);
      if (sc.location && !locs.has(sc.location)) errors.push(`Scene ${sc.id}: unknown location "${sc.location}".`);
      for (const sh of sc.shots) {
        unique('shot', sh.id);
        const at = `Shot ${sh.id}`;
        if (!(sh.duration > 0 && sh.duration <= 120)) errors.push(`${at}: duration must be 0-120 s.`);
        if (doc.mode === 'ai' && sh.type === 'engine') errors.push(`${at}: engine shots need mode "engine" or "mixed".`);
        if (doc.mode === 'engine' && sh.type === 'ai') errors.push(`${at}: AI shots need mode "ai" or "mixed".`);
        for (const c of sh.characters ?? []) if (!chars.has(c)) errors.push(`${at}: unknown character "${c}".`);
        if (sh.location && !locs.has(sh.location)) errors.push(`${at}: unknown location "${sh.location}".`);
        if (sh.music && !music.has(sh.music)) errors.push(`${at}: unknown music track "${sh.music}".`);
        if (sh.continuity && !['previous-last-frame', 'keyframe', 'first-last'].includes(sh.continuity)) errors.push(`${at}: continuity must be previous-last-frame, keyframe or first-last.`);
        if (sh.transition && !['cut', 'crossfade', 'fade-black'].includes(sh.transition.type)) errors.push(`${at}: transition must be cut, crossfade or fade-black.`);
        if (sh.transition && (sh.transition.duration ?? 0) >= sh.duration) errors.push(`${at}: the transition must be shorter than the shot.`);
        const lineIds = new Set<string>();
        for (const l of sh.dialogue ?? []) {
          if (lineIds.has(l.id)) errors.push(`${at}: duplicate line id "${l.id}".`);
          lineIds.add(l.id);
          if (!chars.has(l.character)) errors.push(`${at}: line ${l.id} has unknown character "${l.character}".`);
          if (!l.text.trim()) errors.push(`${at}: line ${l.id} is empty.`);
          if (l.offset !== undefined && (l.offset < 0 || l.offset >= sh.duration)) errors.push(`${at}: line ${l.id} starts outside the shot.`);
        }
        for (const c of sh.sfx ?? []) if (!c.prompt.trim() && !c.preset) errors.push(`${at}: sound cue ${c.id} needs a prompt or preset.`);
        if (sh.type === 'engine' && !sh.camera?.entity) errors.push(`${at}: engine shots need camera.entity (a camera entity id in the scene).`);
      }
    }
  }
  for (const m of doc.audio.music) {
    unique('music', m.id);
    if (m.end !== undefined && m.end <= m.start) errors.push(`Music ${m.id}: end must be after start.`);
  }
  for (const m of doc.audio.ambience) unique('ambience', m.id);
  if (!(doc.render.reanchorEvery >= 1 && doc.render.reanchorEvery <= 12)) errors.push('render.reanchorEvery must be 1-12.');
  return errors;
}

/* ------------------------------------------------------------------ */
/* Walking and timeline                                                */

export interface ShotRef {
  readonly act: Act;
  readonly scene: VideoScene;
  readonly shot: Shot;
  /** Index over the whole video. */
  readonly index: number;
  /** Index inside its scene. */
  readonly inScene: number;
}

export function allShots(doc: VideoDoc): ShotRef[] {
  const out: ShotRef[] = [];
  for (const act of doc.acts)
    for (const scene of act.scenes)
      scene.shots.forEach((shot, inScene) => out.push({ act, scene, shot, index: out.length, inScene }));
  return out;
}

export function findShot(doc: VideoDoc, id: string): ShotRef | undefined {
  return allShots(doc).find((r) => r.shot.id === id);
}

export interface TimelineEntry {
  readonly shotId: string;
  readonly sceneId: string;
  readonly start: number;
  readonly end: number;
  readonly duration: number;
  /** Overlap with the previous shot (crossfade / fade-black), 0 for a cut. */
  readonly overlap: number;
  readonly transition: TransitionType;
}

/** Shots placed on the video timeline: a crossfade overlaps the previous shot by its duration. */
export function shotTimeline(doc: VideoDoc): { entries: TimelineEntry[]; total: number } {
  const entries: TimelineEntry[] = [];
  let cursor = 0;
  for (const ref of allShots(doc)) {
    const sh = ref.shot;
    const type = entries.length ? (sh.transition?.type ?? 'cut') : 'cut';
    const overlap = type === 'cut' ? 0 : Math.min(Math.max(0, sh.transition?.duration ?? 0.5), sh.duration / 2, entries.at(-1)?.duration ?? 0);
    const start = Math.max(0, cursor - overlap);
    const end = start + sh.duration;
    entries.push({ shotId: sh.id, sceneId: ref.scene.id, start, end, duration: sh.duration, overlap, transition: type });
    cursor = end;
  }
  return { entries, total: cursor };
}

/** Frames for `seconds` at `fps`, rounded to the 4n+1 the Wan family needs, at least 5. */
export function framesFor(seconds: number, fps: number, max?: number): number {
  let f = Math.max(5, Math.round(seconds * fps));
  f = Math.round((f - 1) / 4) * 4 + 1;
  if (max !== undefined && f > max) f = Math.floor((max - 1) / 4) * 4 + 1;
  return f;
}

/** HuMo chunk length: 97 frames at 25 fps. */
export const LIPSYNC_CHUNK_SECONDS = 3.84;

/** Splits speech of `seconds` into HuMo calls: `{audioStart, seconds, frames}` (each up to 3.84 s, 4n+1 frames at 25 fps). */
export function lipsyncChunks(seconds: number, chunk = LIPSYNC_CHUNK_SECONDS, fps = 25): { audioStart: number; seconds: number; frames: number }[] {
  const out: { audioStart: number; seconds: number; frames: number }[] = [];
  if (!(seconds > 0)) return out;
  const round = (x: number) => Math.round(x * 1000) / 1000;
  for (let at = 0; at < seconds - 0.05; at += chunk) {
    const len = round(Math.min(chunk, seconds - at));
    out.push({ audioStart: round(at), seconds: len, frames: framesFor(len, fps, 97) });
  }
  return out;
}

/** Rough speaking time of a line when its WAV is not made yet. */
export function estimateSpeechSeconds(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.round((words / 2.6 + 0.4) * 100) / 100;
}

export interface LineTiming {
  readonly shotId: string;
  readonly lineId: string;
  readonly character: string;
  readonly text: string;
  /** Shot-local seconds. */
  readonly offset: number;
  /** Timeline seconds. */
  readonly start: number;
  readonly end: number;
  readonly estimated: boolean;
}

/**
 * When every line is spoken: an explicit `offset`, otherwise 0.3 s into the shot or 0.25 s after the previous
 * line. `seconds(line)` gives the real WAV length when known (else estimated from the words).
 */
export function lineTimings(doc: VideoDoc, seconds: (shot: Shot, line: DialogueLine) => number | null = () => null): LineTiming[] {
  const { entries } = shotTimeline(doc);
  const out: LineTiming[] = [];
  for (const ref of allShots(doc)) {
    const t = entries[ref.index]!;
    let next = 0.3;
    for (const line of ref.shot.dialogue ?? []) {
      const real = seconds(ref.shot, line);
      const len = real ?? estimateSpeechSeconds(line.text);
      const offset = line.offset ?? next;
      out.push({ shotId: ref.shot.id, lineId: line.id, character: line.character, text: line.text, offset, start: t.start + offset, end: t.start + offset + len, estimated: real === null });
      next = offset + len + 0.25;
    }
  }
  return out;
}

/** SubRip text of the given lines (speaker names in front unless `names` is false). */
export function toSrt(lines: readonly Pick<LineTiming, 'start' | 'end' | 'text' | 'character'>[], names: (id: string) => string = (id) => id, withNames = true): string {
  const stamp = (t: number) => {
    const ms = Math.max(0, Math.round(t * 1000));
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const sec = Math.floor((ms % 60_000) / 1000);
    const pad = (x: number, w = 2) => String(x).padStart(w, '0');
    return `${pad(h)}:${pad(m)}:${pad(sec)},${pad(ms % 1000, 3)}`;
  };
  return [...lines]
    .sort((a, b) => a.start - b.start)
    .map((l, i) => `${i + 1}\n${stamp(l.start)} --> ${stamp(l.end)}\n${withNames ? `${names(l.character)}: ` : ''}${l.text}\n`)
    .join('\n');
}

/* ------------------------------------------------------------------ */
/* Files                                                               */

export function videoOutputDir(doc: Pick<VideoDoc, 'outputDir'>, docPath: string): string {
  return (doc.outputDir ?? `videos/${videoDocSlug(docPath)}`).replace(/\\/g, '/').replace(/\/+$/, '');
}

/** Project paths of every generated piece. */
export const videoPaths = {
  sheet: (dir: string, charId: string) => `${dir}/characters/${charId}.png`,
  voiceTest: (dir: string, charId: string) => `${dir}/characters/${charId}_voice.wav`,
  keyframe: (dir: string, shotId: string) => `${dir}/shots/${shotId}/keyframe.png`,
  endKeyframe: (dir: string, shotId: string) => `${dir}/shots/${shotId}/keyframe_end.png`,
  animatic: (dir: string, shotId: string) => `${dir}/shots/${shotId}/animatic.mp4`,
  final: (dir: string, shotId: string) => `${dir}/shots/${shotId}/final.mp4`,
  post: (dir: string, shotId: string) => `${dir}/shots/${shotId}/post.mp4`,
  engine: (dir: string, shotId: string) => `${dir}/shots/${shotId}/engine.mp4`,
  edit: (dir: string, shotId: string) => `${dir}/shots/${shotId}/edit.mp4`,
  voice: (dir: string, shotId: string, lineId: string) => `${dir}/audio/voice/${shotId}_${lineId}.wav`,
  lipsync: (dir: string, shotId: string, lineId: string) => `${dir}/shots/${shotId}/lipsync_${lineId}.mp4`,
  sfx: (dir: string, shotId: string, cueId: string) => `${dir}/audio/sfx/${shotId}_${cueId}.wav`,
  music: (dir: string, trackId: string) => `${dir}/audio/music/${trackId}.wav`,
  ambience: (dir: string, trackId: string) => `${dir}/audio/ambience/${trackId}.wav`,
  final_video: (dir: string, slug: string) => `${dir}/${slug}.mp4`,
  srt: (dir: string, slug: string) => `${dir}/${slug}.srt`,
  /** The last frame a generator saves next to a clip (chaining). */
  lastFrame: (clip: string) => clip.replace(/\.mp4$/i, '.last.png'),
};

/** Default sequence path for the engine shots. */
export function videoSequencePath(doc: VideoDoc, docPath: string): string {
  return doc.engine?.sequence ?? `sequences/${videoDocSlug(docPath)}.sequence.json`;
}

/* ------------------------------------------------------------------ */
/* Status from requests                                                */

const STATE_OF: Record<GenStatus, StageState> = {
  'awaiting-approval': 'awaiting-approval',
  rejected: 'denied',
  queued: 'queued',
  running: 'running',
  review: 'review',
  accepted: 'done',
  denied: 'denied',
  redo: 'queued',
  refined: 'queued',
  failed: 'failed',
};

/** Follows redo / refine links to the newest request of a chain. */
export function latestRequest(id: string, requests: readonly Pick<GenRequest, 'id' | 'redoOf' | 'refineOf' | 'status'>[]): string {
  let current = id;
  for (let guard = 0; guard < 50; guard++) {
    const next = requests.find((r) => r.redoOf === current || r.refineOf === current);
    if (!next) return current;
    current = next.id;
  }
  return current;
}

/**
 * Live state of a piece: the newest request of its redo / refine chain decides; without a request (or when the
 * request is gone) the cached status, or "done" for a file given by hand.
 */
export function stageState(record: StageRecord | undefined, requests: readonly GenRequest[]): StageState {
  if (!record) return 'none';
  if (record.request) {
    const id = latestRequest(record.request, requests);
    const req = requests.find((r) => r.id === id);
    if (req) return STATE_OF[req.status];
  }
  return record.status ?? (record.file ? 'done' : 'none');
}

/** The request id a piece is currently tracked by (newest of its chain). */
export function stageRequestId(record: StageRecord | undefined, requests: readonly GenRequest[]): string | undefined {
  return record?.request ? latestRequest(record.request, requests) : undefined;
}

export const STAGE_STATE_LABEL: Record<StageState, string> = {
  none: 'Not made',
  'awaiting-approval': 'Awaiting approval',
  queued: 'Queued',
  running: 'Running',
  review: 'Ready for review',
  done: 'Done',
  denied: 'Denied',
  failed: 'Failed',
};

/* ------------------------------------------------------------------ */
/* Render groups                                                       */

/** What a render run makes, in pipeline order. */
export const RENDER_GROUPS = ['characters', 'keyframes', 'animatic', 'final', 'engine', 'voice', 'lipsync', 'sfx', 'music', 'post', 'assemble'] as const;
export type RenderGroup = (typeof RENDER_GROUPS)[number];

/** Presets of the top-bar buttons. */
export const RENDER_PRESETS: Readonly<Record<'animatic' | 'final' | 'audio' | 'assemble' | 'all', readonly RenderGroup[]>> = {
  animatic: ['characters', 'keyframes', 'animatic'],
  final: ['characters', 'keyframes', 'final', 'engine', 'voice', 'lipsync', 'post'],
  audio: ['voice', 'sfx', 'music'],
  assemble: ['assemble'],
  all: ['characters', 'keyframes', 'final', 'engine', 'voice', 'lipsync', 'sfx', 'music', 'post', 'assemble'],
};

/** Rough seconds per piece on the 8 GB / 16 GB machine (README; unmeasured numbers are estimates). */
export const STAGE_SECONDS: Readonly<Record<string, number>> = {
  keyframe: 40,
  animatic: 120,
  'animatic-extend': 120,
  shot: 570,
  'shot-extend': 570,
  'shot-hq': 1500,
  'shot-bridge': 1500,
  'shot-hq-extend': 1500,
  lipsync: 240,
  interpolate: 5,
  'interpolate-film': 20,
  upscale: 600,
  'upscale-fast': 300,
  'engine-record': 60,
  voice: 10,
  sfx: 40,
  music: 120,
  assemble: 120,
};

/** Rough wall time of a full final render (seconds): finals, lipsync, audio and assembly. */
export function estimateRenderSeconds(doc: VideoDoc, group: 'animatic' | 'final' = 'final'): number {
  let total = 0;
  for (const { shot } of allShots(doc)) {
    if (shot.type === 'engine') {
      total += STAGE_SECONDS['engine-record']!;
      continue;
    }
    const gen = group === 'animatic' ? (shot.generators?.animatic ?? doc.render.animatic) : (shot.generators?.final ?? doc.render.final);
    const clips = Math.max(1, Math.ceil(shot.duration / 5));
    total += STAGE_SECONDS.keyframe! / 2 + clips * (STAGE_SECONDS[gen] ?? 570);
    if (group === 'final') {
      for (const line of shot.dialogue ?? []) {
        total += STAGE_SECONDS.voice!;
        if (line.onCamera !== false && (shot.characters ?? []).includes(line.character))
          total += Math.ceil(estimateSpeechSeconds(line.text) / LIPSYNC_CHUNK_SECONDS) * STAGE_SECONDS.lipsync!;
      }
      total += (shot.sfx?.length ?? 0) * STAGE_SECONDS.sfx!;
      if (doc.render.upscale !== 'none') total += STAGE_SECONDS[doc.render.upscale === 'fast' ? 'upscale-fast' : 'upscale']!;
    }
  }
  if (group === 'final') total += doc.audio.music.length * STAGE_SECONDS.music! + STAGE_SECONDS.assemble!;
  return Math.round(total);
}

/** "2 h 15 min" style duration. */
export function durationLabel(seconds: number): string {
  const sec = Math.max(0, Math.round(seconds));
  if (sec < 90) return `${sec} s`;
  const min = Math.round(sec / 60);
  if (min < 90) return `${min} min`;
  return `${Math.floor(min / 60)} h ${min % 60} min`;
}

/* ------------------------------------------------------------------ */
/* JSON schema (documentation and editor tooling)                      */

const STAGE_RECORD_SCHEMA = {
  type: 'object',
  description: 'Which generation request made this piece (written by the pipeline).',
  properties: { request: { type: 'string' }, file: { type: 'string' }, status: { type: 'string' }, by: { type: 'string' }, updatedAt: { type: 'string' }, note: { type: 'string' } },
};

export const VIDEO_DOC_SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  title: 'Prism video storyboard (prism.video/1)',
  type: 'object',
  required: ['format', 'name', 'mode', 'targetLength', 'width', 'height', 'fps', 'bible', 'characters', 'acts'],
  properties: {
    format: { const: VIDEO_FORMAT },
    name: { type: 'string' },
    mode: { enum: ['ai', 'engine', 'mixed'] },
    prompt: { type: 'string' },
    targetLength: { type: 'number', description: 'Seconds' },
    width: { type: 'integer' },
    height: { type: 'integer' },
    fps: { type: 'number' },
    outputDir: { type: 'string' },
    bible: {
      type: 'object',
      properties: { logline: { type: 'string' }, synopsis: { type: 'string' }, tone: { type: 'string' }, visualStyle: { type: 'string' }, worldRules: { type: 'array', items: { type: 'string' } }, negative: { type: 'string' } },
    },
    characters: {
      type: 'array',
      items: {
        type: 'object',
        required: ['id', 'name', 'description'],
        properties: {
          id: { type: 'string' },
          name: { type: 'string' },
          description: { type: 'string' },
          refImages: { type: 'array', items: { type: 'string' } },
          seed: { type: 'integer' },
          voice: {
            type: 'object',
            required: ['engine'],
            properties: { engine: { enum: ['kokoro', 'chatterbox'] }, voice: { type: 'string' }, reference: { type: 'string' }, style: { type: 'string' }, speed: { type: 'number' }, exaggeration: { type: 'number' }, cfg: { type: 'number' }, lang: { type: 'string' } },
          },
          stages: { type: 'object', properties: { sheet: STAGE_RECORD_SCHEMA, voiceTest: STAGE_RECORD_SCHEMA } },
        },
      },
    },
    locations: { type: 'array', items: { type: 'object', required: ['id', 'name', 'description'], properties: { id: { type: 'string' }, name: { type: 'string' }, description: { type: 'string' }, refImage: { type: 'string' } } } },
    acts: {
      type: 'array',
      items: {
        type: 'object',
        required: ['id', 'title', 'scenes'],
        properties: {
          id: { type: 'string' },
          title: { type: 'string' },
          summary: { type: 'string' },
          scenes: {
            type: 'array',
            items: {
              type: 'object',
              required: ['id', 'title', 'shots'],
              properties: {
                id: { type: 'string' },
                title: { type: 'string' },
                synopsis: { type: 'string' },
                location: { type: 'string' },
                shots: {
                  type: 'array',
                  items: {
                    type: 'object',
                    required: ['id', 'duration', 'type', 'description'],
                    properties: {
                      id: { type: 'string' },
                      title: { type: 'string' },
                      duration: { type: 'number' },
                      type: { enum: ['ai', 'engine'] },
                      description: { type: 'string' },
                      prompt: { type: 'string' },
                      negative: { type: 'string' },
                      camera: { type: 'object', description: 'framing, movement, lens; engine shots: entity, lookAt, lookAtOffset, position/rotation/fov keys, shake' },
                      characters: { type: 'array', items: { type: 'string' } },
                      location: { type: 'string' },
                      dialogue: {
                        type: 'array',
                        items: {
                          type: 'object',
                          required: ['id', 'character', 'text'],
                          properties: { id: { type: 'string' }, character: { type: 'string' }, text: { type: 'string' }, emotion: { type: 'string' }, offset: { type: 'number' }, onCamera: { type: 'boolean' }, stages: { type: 'object' } },
                        },
                      },
                      sfx: { type: 'array', items: { type: 'object', required: ['id', 'prompt'], properties: { id: { type: 'string' }, prompt: { type: 'string' }, preset: { type: 'string' }, offset: { type: 'number' }, duration: { type: 'number' }, volume: { type: 'number' } } } },
                      music: { type: 'string' },
                      continuity: { enum: ['previous-last-frame', 'keyframe', 'first-last'] },
                      transition: { type: 'object', properties: { type: { enum: ['cut', 'crossfade', 'fade-black'] }, duration: { type: 'number' } } },
                      keyframe: { type: 'string' },
                      endKeyframe: { type: 'string' },
                      summary: { type: 'string' },
                      generators: { type: 'object', properties: { animatic: { type: 'string' }, final: { type: 'string' }, post: { type: 'array', items: { type: 'string' } }, keyframe: { type: 'array', items: { type: 'string' } } } },
                      seed: { type: 'integer' },
                      engine: { type: 'object', properties: { scene: { type: 'string' }, animations: { type: 'array' }, transforms: { type: 'array' }, tracks: { type: 'array' } } },
                      stages: { type: 'object', description: 'keyframe, endKeyframe, animatic, final, post, engine: StageRecord' },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    audio: {
      type: 'object',
      properties: {
        music: { type: 'array', items: { type: 'object', required: ['id', 'prompt', 'start'], properties: { id: { type: 'string' }, prompt: { type: 'string' }, style: { type: 'string' }, lyrics: { type: 'string' }, start: { type: 'number' }, end: { type: 'number' }, volume: { type: 'number' }, fadeIn: { type: 'number' }, fadeOut: { type: 'number' }, duck: { type: 'boolean' } } } },
        ambience: { type: 'array', items: { type: 'object', required: ['id', 'prompt', 'start'], properties: { id: { type: 'string' }, prompt: { type: 'string' }, preset: { type: 'string' }, start: { type: 'number' }, end: { type: 'number' }, volume: { type: 'number' }, loop: { type: 'boolean' } } } },
        dialogueVolume: { type: 'number' },
        musicVolume: { type: 'number' },
        sfxVolume: { type: 'number' },
        loudness: { type: 'number', description: 'LUFS target' },
      },
    },
    render: {
      type: 'object',
      properties: {
        quality: { enum: ['draft', 'standard', 'high'] },
        reanchorEvery: { type: 'integer', minimum: 1, maximum: 12 },
        animatic: { type: 'string' },
        final: { type: 'string' },
        interpolate: { type: 'boolean' },
        upscale: { enum: ['none', 'fast', 'seedvr2'] },
        subtitles: { type: 'boolean' },
        keyframeStyle: { enum: ['photo', 'photo-fast', 'illustration'] },
        allowDraftClips: { type: 'boolean' },
      },
    },
    engine: { type: 'object', properties: { scene: { type: 'string' }, sequence: { type: 'string' } } },
    output: { type: 'object', properties: { file: { type: 'string' }, srt: { type: 'string' }, stages: { type: 'object' } } },
    editor: { type: 'object' },
  },
} as const;

/* ------------------------------------------------------------------ */
/* Stage records: where each piece's record lives                      */

export type ShotStage = 'keyframe' | 'endKeyframe' | 'animatic' | 'final' | 'post' | 'engine';

/** The piece a stage record belongs to. */
export type StageTarget =
  | { readonly type: 'character'; readonly id: string; readonly stage: 'sheet' | 'voiceTest' }
  | { readonly type: 'shot'; readonly id: string; readonly stage: ShotStage }
  | { readonly type: 'line'; readonly shot: string; readonly id: string; readonly stage: 'voice' | 'lipsync' }
  | { readonly type: 'sfx'; readonly shot: string; readonly id: string }
  | { readonly type: 'music'; readonly id: string }
  | { readonly type: 'ambience'; readonly id: string }
  | { readonly type: 'output' };

/** Stable key of a target ("shot:s1:final", "line:s1:l2:voice"). */
export function targetKey(t: StageTarget): string {
  switch (t.type) {
    case 'character':
      return `character:${t.id}:${t.stage}`;
    case 'shot':
      return `shot:${t.id}:${t.stage}`;
    case 'line':
      return `line:${t.shot}:${t.id}:${t.stage}`;
    case 'sfx':
      return `sfx:${t.shot}:${t.id}`;
    case 'music':
      return `music:${t.id}`;
    case 'ambience':
      return `ambience:${t.id}`;
    default:
      return 'output:assemble';
  }
}

const SHOT_STAGES: readonly ShotStage[] = ['keyframe', 'endKeyframe', 'animatic', 'final', 'post', 'engine'];

/** Parses a key back into a target (null when malformed). */
export function parseTargetKey(key: string): StageTarget | null {
  const p = key.split(':');
  if (p[0] === 'character' && p.length === 3 && (p[2] === 'sheet' || p[2] === 'voiceTest')) return { type: 'character', id: p[1]!, stage: p[2] };
  if (p[0] === 'shot' && p.length === 3 && SHOT_STAGES.includes(p[2] as ShotStage)) return { type: 'shot', id: p[1]!, stage: p[2] as ShotStage };
  if (p[0] === 'line' && p.length === 4 && (p[3] === 'voice' || p[3] === 'lipsync')) return { type: 'line', shot: p[1]!, id: p[2]!, stage: p[3] };
  if (p[0] === 'sfx' && p.length === 3) return { type: 'sfx', shot: p[1]!, id: p[2]! };
  if (p[0] === 'music' && p.length === 2) return { type: 'music', id: p[1]! };
  if (p[0] === 'ambience' && p.length === 2) return { type: 'ambience', id: p[1]! };
  if (key === 'output:assemble') return { type: 'output' };
  return null;
}

export function getStageRecord(doc: VideoDoc, t: StageTarget): StageRecord | undefined {
  switch (t.type) {
    case 'character':
      return doc.characters.find((c) => c.id === t.id)?.stages?.[t.stage];
    case 'shot':
      return findShot(doc, t.id)?.shot.stages?.[t.stage];
    case 'line':
      return findShot(doc, t.shot)?.shot.dialogue?.find((l) => l.id === t.id)?.stages?.[t.stage];
    case 'sfx':
      return findShot(doc, t.shot)?.shot.sfx?.find((c) => c.id === t.id)?.stages?.sfx;
    case 'music':
      return doc.audio.music.find((m) => m.id === t.id)?.stages?.music;
    case 'ambience':
      return doc.audio.ambience.find((m) => m.id === t.id)?.stages?.sfx;
    default:
      return doc.output?.stages?.assemble;
  }
}

type WithStages = { readonly stages?: object };

/** A copy of the doc with the record of `t` replaced (null removes it). Unknown targets leave the doc unchanged. */
export function setStageRecord(doc: VideoDoc, t: StageTarget, record: StageRecord | null): VideoDoc {
  const put = <T extends WithStages>(item: T, key: string): T => {
    const stages = { ...((item.stages ?? {}) as Record<string, unknown>) };
    if (record) stages[key] = record;
    else delete stages[key];
    return { ...item, stages };
  };
  const mapShots = (fn: (shot: Shot) => Shot): VideoDoc => ({
    ...doc,
    acts: doc.acts.map((a) => ({ ...a, scenes: a.scenes.map((sc) => ({ ...sc, shots: sc.shots.map(fn) })) })),
  });
  switch (t.type) {
    case 'character':
      return { ...doc, characters: doc.characters.map((c) => (c.id === t.id ? put(c, t.stage) : c)) };
    case 'shot':
      return mapShots((sh) => (sh.id === t.id ? put(sh, t.stage) : sh));
    case 'line':
      return mapShots((sh) => (sh.id === t.shot ? { ...sh, dialogue: (sh.dialogue ?? []).map((l) => (l.id === t.id ? put(l, t.stage) : l)) } : sh));
    case 'sfx':
      return mapShots((sh) => (sh.id === t.shot ? { ...sh, sfx: (sh.sfx ?? []).map((c) => (c.id === t.id ? put(c, 'sfx') : c)) } : sh));
    case 'music':
      return { ...doc, audio: { ...doc.audio, music: doc.audio.music.map((m) => (m.id === t.id ? put(m, 'music') : m)) } };
    case 'ambience':
      return { ...doc, audio: { ...doc.audio, ambience: doc.audio.ambience.map((m) => (m.id === t.id ? put(m, 'sfx') : m)) } };
    default: {
      const output = { ...(doc.output ?? {}) };
      const stages: Record<string, StageRecord> = { ...(output.stages ?? {}) };
      if (record) stages.assemble = record;
      else delete stages.assemble;
      return { ...doc, output: { ...output, stages } };
    }
  }
}

/** Every stage record of a doc, by target key. */
export function stageRecords(doc: VideoDoc): Map<string, StageRecord> {
  const out = new Map<string, StageRecord>();
  const add = (t: StageTarget) => {
    const r = getStageRecord(doc, t);
    if (r) out.set(targetKey(t), r);
  };
  for (const c of doc.characters) for (const stage of ['sheet', 'voiceTest'] as const) add({ type: 'character', id: c.id, stage });
  for (const { shot } of allShots(doc)) {
    for (const stage of SHOT_STAGES) add({ type: 'shot', id: shot.id, stage });
    for (const l of shot.dialogue ?? []) for (const stage of ['voice', 'lipsync'] as const) add({ type: 'line', shot: shot.id, id: l.id, stage });
    for (const c of shot.sfx ?? []) add({ type: 'sfx', shot: shot.id, id: c.id });
  }
  for (const m of doc.audio.music) add({ type: 'music', id: m.id });
  for (const m of doc.audio.ambience) add({ type: 'ambience', id: m.id });
  add({ type: 'output' });
  return out;
}

/**
 * Keeps the pipeline's stage records of `from` in `next` wherever `next` has the same piece (same ids) but no
 * record of its own. Writers of the story (the editor, the assistant) use it so a storyboard edit never forgets
 * which request made which file.
 */
export function preserveStageRecords(next: VideoDoc, from: VideoDoc): VideoDoc {
  let out = next;
  for (const [key, record] of stageRecords(from)) {
    const t = parseTargetKey(key);
    if (t && getStageRecord(out, t) === undefined) out = setStageRecord(out, t, record);
  }
  return out;
}

/** Pipeline-owned records win: the records of `from` (disk) replace those of `next` (an editor's possibly stale copy). */
export function adoptStageRecords(next: VideoDoc, from: VideoDoc): VideoDoc {
  let out = next;
  for (const [key, record] of stageRecords(from)) {
    const t = parseTargetKey(key);
    if (t) out = setStageRecord(out, t, record);
  }
  return out;
}

/** Which clip of a shot goes into the cut: post > final > engine > (animatic when drafts are allowed). */
export function bestClipStage(shot: Shot, state: (stage: ShotStage) => StageState, allowDraft = false): ShotStage | null {
  const order: ShotStage[] = shot.type === 'engine' ? ['engine'] : ['post', 'final'];
  if (allowDraft && shot.type !== 'engine') order.push('animatic');
  return order.find((st) => state(st) === 'done') ?? null;
}

/** Project path of a shot stage's file. */
export function shotStagePath(dir: string, shotId: string, stage: ShotStage): string {
  return videoPaths[stage](dir, shotId);
}
